import { createSignal, onMount, Show, For, createMemo } from 'solid-js';
import { useParams } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import BotManageNav from '../components/BotManageNav';
import { BOT_ACTIONS, can } from '../services/resourceActions';
import type { BotRegistryDetailResponse } from '../api/generated/schemas';
import styles from './BotProfile.module.css';
import { firstError } from '../api/envelope';

interface Skin {
    id: string;
    state: string;
    url: string;
    textureKey?: string;
    variant: string;
    alias?: string;
}

interface BotProfile {
    id: string;
    name: string;
    skins: Skin[];
}

interface NameChangeInfo {
    nameChangeAllowed: boolean;
    changedAt: string;
    createdAt: string;
    nextChangeAvailableAt: string | null;
}


const VARIANTS: { value: string; label: string }[] = [
    { value: 'CLASSIC', label: 'CLASSIC' },
    { value: 'SLIM', label: 'SLIM' },
];

export default function BotProfile() {
    const params = useParams();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [botState, setBotState] = createSignal<BotRegistryDetailResponse | null>(null);
    const [profile, setProfile] = createSignal<BotProfile | null>(null);
    const [nameInfo, setNameInfo] = createSignal<NameChangeInfo | null>(null);
    const [loading, setLoading] = createSignal(true);
    const [busy, setBusy] = createSignal(false);

    const [variant, setVariant] = createSignal<'CLASSIC' | 'SLIM'>('CLASSIC');
    const [skinUrl, setSkinUrl] = createSignal('');
    const [skinFile, setSkinFile] = createSignal<File | null>(null);
    const [dragOver, setDragOver] = createSignal(false);

    const [newName, setNewName] = createSignal('');
    const [availability, setAvailability] = createSignal<string | null>(null);
    const [availabilityError, setAvailabilityError] = createSignal(false);
    let availabilityTimer: number | undefined;

    const botId = () => params.id || '';
    // Skin, cape and name changes all go through BotProfileController, which requires
    // bot.credentials.manage. This used to test an access-level ordinal that no longer exists on
    // the DTO, so it read as 0 and locked the page for everyone, owners included.
    const canManageProfile = () => can(botState()?.myActions, BOT_ACTIONS.CredentialsManage);
    const isOnline = () => {
        const s = botState()?.state;
        return s === 'Connecting' || s === 'Playing' || s === 'Reconnecting' || s === 'Disconnecting';
    };
    const canMutate = () => canManageProfile() && !isOnline();

    const activeSkin = createMemo(() => profile()?.skins.find(s => s.state === 'ACTIVE') ?? profile()?.skins[0]);

    onMount(async () => {
        await Promise.all([loadBotState(), loadProfile(), loadNameInfo()]);
        setLoading(false);
        if (activeSkin()) {
            const v = activeSkin()!.variant?.toUpperCase();
            if (v === 'SLIM' || v === 'CLASSIC') setVariant(v);
        }
    });

    async function loadBotState() {
        const res = await api.botRegistry.getBot(botId());
        if (isOk(res.data)) setBotState(res.data.value);
        else toast?.error(firstError(res.data) ?? t('BOT_PROFILE_LOAD_FAILED'));
    }

    async function loadProfile() {
        const res = await api.botProfile.getProfile(botId());
        if (isOk(res.data)) setProfile(res.data.value as unknown as BotProfile);
        else toast?.error(firstError(res.data) ?? t('BOT_PROFILE_LOAD_FAILED'));
    }

    async function loadNameInfo() {
        const res = await api.botProfile.getNameCooldown(botId());
        if (isOk(res.data)) setNameInfo(res.data.value as unknown as NameChangeInfo);
        else toast?.error(firstError(res.data) ?? t('BOT_PROFILE_LOAD_FAILED'));
    }

    async function setSkinViaUrl() {
        if (!skinUrl().trim()) {
            toast?.error(t('BOT_PROFILE_SKIN_URL_REQUIRED'));
            return;
        }
        setBusy(true);
        const res = await api.botProfile.setSkinFromUrl(botId(), { variant: variant(), url: skinUrl().trim() });
        setBusy(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_PROFILE_SKIN_UPDATED'));
            setProfile(res.data.value as unknown as BotProfile);
            setSkinUrl('');
        } else {
            toast?.error(firstError(res.data) ?? t('BOT_PROFILE_SKIN_SET_FAILED'));
        }
    }

    async function setSkinViaFile() {
        const file = skinFile();
        if (!file) {
            toast?.error(t('BOT_PROFILE_PNG_REQUIRED'));
            return;
        }
        setBusy(true);
        try {
            const res = await api.botProfile.setSkinFromFile(botId(), { variant: variant(), file });
            if (isOk(res.data)) {
                toast?.info(t('BOT_PROFILE_SKIN_UPDATED'));
                setProfile(res.data.value as unknown as BotProfile);
                setSkinFile(null);
            } else {
                toast?.error(firstError(res.data) ?? t('BOT_PROFILE_SKIN_UPLOAD_FAILED'));
            }
        } finally {
            setBusy(false);
        }
    }

    async function resetSkin() {
        if (!await showConfirm(t('BOT_PROFILE_RESET_SKIN_CONFIRM'), true)) return;
        setBusy(true);
        const res = await api.botProfile.resetSkin(botId());
        setBusy(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_PROFILE_SKIN_RESET'));
            setProfile(res.data.value as unknown as BotProfile);
        } else {
            toast?.error(firstError(res.data) ?? t('BOT_PROFILE_SKIN_RESET_FAILED'));
        }
    }

    function onNameInput(value: string) {
        setNewName(value);
        setAvailability(null);
        setAvailabilityError(false);
        if (availabilityTimer) clearTimeout(availabilityTimer);
        if (!value.trim() || value.trim() === profile()?.name) return;
        availabilityTimer = window.setTimeout(async () => {
            const res = await api.botProfile.checkNameAvailable(botId(), { name: value.trim() });
            if (isOk(res.data)) setAvailability(res.data.value.status);
            else setAvailabilityError(true);
        }, 400);
    }

    async function changeName() {
        const name = newName().trim();
        if (!name || name === profile()?.name) return;
        if (availability() !== 'AVAILABLE') {
            toast?.error(t('BOT_PROFILE_NAME_UNAVAILABLE'));
            return;
        }
        const ok = await showConfirm(t('BOT_PROFILE_NAME_CHANGE_CONFIRM', name), true);
        if (!ok) return;
        setBusy(true);
        const res = await api.botProfile.changeName(botId(), { name });
        setBusy(false);
        if (isOk(res.data)) {
            toast?.info(t('BOT_PROFILE_NAME_CHANGED'));
            setProfile(res.data.value as unknown as BotProfile);
            setNewName('');
            setAvailability(null);
            void loadNameInfo();
            void loadBotState();
        } else {
            toast?.error(firstError(res.data) ?? t('BOT_PROFILE_NAME_CHANGE_FAILED'));
        }
    }

    function onFilePicked(files: FileList | null) {
        const f = files?.[0];
        if (!f) return;
        if (!f.type.includes('png') && !f.name.toLowerCase().endsWith('.png')) {
            toast?.error(t('BOT_PROFILE_PNG_ONLY'));
            return;
        }
        if (f.size > 1_000_000) {
            toast?.error(t('BOT_PROFILE_MAX_1MB'));
            return;
        }
        setSkinFile(f);
    }

    function onDrop(e: DragEvent) {
        e.preventDefault();
        setDragOver(false);
        onFilePicked(e.dataTransfer?.files ?? null);
    }

    return (
        <div class={styles.container}>
            <BotManageNav botId={botId()} />

            <Show when={!loading()} fallback={
                <div class={styles.loadingCenter}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            }>
                <Show when={!canManageProfile()}>
                    <div class={`${styles.banner} ${styles.bannerLocked}`}>
                        <i class="fa-solid fa-lock" />
                        <Text val="BOT_PROFILE_ADMIN_ONLY" />
                    </div>
                </Show>
                <Show when={canManageProfile() && isOnline()}>
                    <div class={styles.banner}>
                        <i class="fa-solid fa-triangle-exclamation" />
                        <Text val="BOT_PROFILE_ONLINE_BANNER" />
                    </div>
                </Show>

                <div class="fcard v">
                    <h2 class={styles.sectionHeader}>
                        <i class="fa-solid fa-shirt" /> <Text val="BOT_PROFILE_SKIN" />
                    </h2>
                    <div class={styles.skinRow}>
                        <div class={styles.skinPreview}>
                            <Show when={profile()?.id} fallback={<span><Text val="LOADING" /></span>}>
                                <img
                                    class={styles.skinImage}
                                    src={`https://mc-heads.net/body/${profile()!.id}/200`}
                                    alt={profile()!.name}
                                />
                                <Show when={activeSkin()}>
                                    <span class={styles.skinMeta}>
                                        {activeSkin()!.variant}{activeSkin()!.alias ? ` · ${activeSkin()!.alias}` : ''}
                                    </span>
                                </Show>
                            </Show>
                        </div>

                        <div class={styles.skinForm}>
                            <div>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                    <Text val="BOT_PROFILE_VARIANT" />
                                </span>
                                <div class={styles.variantToggle}>
                                    <For each={VARIANTS}>{v => (
                                        <button
                                            type="button"
                                            class={`btn btn-secondary ${styles.variantBtn} ${variant() === v.value ? styles.variantBtnActive : ''}`}
                                            disabled={!canMutate() || busy()}
                                            onClick={() => setVariant(v.value as 'CLASSIC' | 'SLIM')}
                                        >
                                            {v.label}
                                        </button>
                                    )}</For>
                                </div>
                            </div>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                    <Text val="BOT_PROFILE_SKIN_URL" />
                                </span>
                                <input
                                    type="url"
                                    placeholder={t('BOT_PROFILE_URL_PLACEHOLDER')}
                                    value={skinUrl()}
                                    onInput={e => setSkinUrl(e.currentTarget.value)}
                                    disabled={!canMutate() || busy()}
                                />
                            </label>
                            <div class={styles.actions}>
                                <button
                                    class="btn btn-primary"
                                    disabled={!canMutate() || busy() || !skinUrl().trim()}
                                    onClick={setSkinViaUrl}
                                >
                                    <Text val="BOT_PROFILE_APPLY_URL" />
                                </button>
                            </div>

                            <div
                                class={`${styles.dropZone} ${dragOver() ? styles.dropZoneActive : ''}`}
                                onClick={() => {
                                    if (!canMutate() || busy()) return;
                                    document.getElementById('skinFileInput')?.click();
                                }}
                                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={onDrop}
                            >
                                <i class="fa-solid fa-cloud-arrow-up fa-2x" />
                                <div><Text val="BOT_PROFILE_DROP_PNG" /></div>
                                <Show when={skinFile()}>
                                    <div class={styles.fileName}>{skinFile()!.name}</div>
                                </Show>
                            </div>
                            <input
                                id="skinFileInput"
                                type="file"
                                accept="image/png"
                                style={{ display: 'none' }}
                                onChange={e => onFilePicked(e.currentTarget.files)}
                            />
                            <div class={styles.actions}>
                                <button
                                    class="btn btn-danger"
                                    disabled={!canMutate() || busy()}
                                    onClick={resetSkin}
                                >
                                    <i class="fa-solid fa-rotate-left" /> <Text val="BOT_PROFILE_RESET_SKIN" />
                                </button>
                                <button
                                    class="btn btn-primary"
                                    disabled={!canMutate() || busy() || !skinFile()}
                                    onClick={setSkinViaFile}
                                >
                                    <Text val="BOT_PROFILE_UPLOAD_FILE" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="fcard v">
                    <h2 class={styles.sectionHeader}>
                        <i class="fa-solid fa-id-card" /> <Text val="BOT_PROFILE_NAME" />
                    </h2>

                    <Show when={nameInfo()}>
                        <div class={styles.cooldownInfo}>
                            <Show
                                when={nameInfo()!.nameChangeAllowed}
                                fallback={
                                    <span class={styles.cooldownLocked}>
                                        <i class="fa-solid fa-lock" />{' '}
                                        <Text val="BOT_PROFILE_COOLDOWN_LOCKED" />:{' '}
                                        {formatDateTime(nameInfo()!.nextChangeAvailableAt!)}
                                    </span>
                                }
                            >
                                <span class={styles.cooldownAvailable}>
                                    <i class="fa-solid fa-check" /> <Text val="BOT_PROFILE_COOLDOWN_AVAILABLE" />
                                </span>
                            </Show>
                        </div>
                    </Show>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="BOT_PROFILE_NEW_NAME" />
                        </span>
                        <div class={styles.nameInputRow}>
                            <input
                                type="text"
                                value={newName()}
                                placeholder={profile()?.name ?? ''}
                                onInput={e => onNameInput(e.currentTarget.value)}
                                disabled={!canMutate() || busy() || !(nameInfo()?.nameChangeAllowed ?? false)}
                            />
                            <button
                                class="btn btn-primary"
                                disabled={
                                    !canMutate() || busy() ||
                                    !newName().trim() || newName().trim() === profile()?.name ||
                                    availability() !== 'AVAILABLE' ||
                                    !(nameInfo()?.nameChangeAllowed ?? false)
                                }
                                onClick={changeName}
                            >
                                <Text val="BOT_PROFILE_CHANGE_NAME" />
                            </button>
                        </div>
                        <Show when={availability()}>
                            <span class={`${styles.availabilityHint} ${availability() === 'AVAILABLE' ? styles.availabilityHintOk : styles.availabilityHintBad}`}>
                                {availability() === 'AVAILABLE' ? '✓ ' : '✗ '}{availability()}
                            </span>
                        </Show>
                        <Show when={availabilityError()}>
                            <span class={`${styles.availabilityHint} ${styles.availabilityHintBad}`}>
                                <i class="fa-solid fa-triangle-exclamation" /> <Text val="BOT_PROFILE_NAME_CHECK_FAILED" />
                            </span>
                        </Show>
                    </label>
                </div>
            </Show>
        </div>
    );
}
